import React, { useState, useEffect } from 'react';
import { Card, Tabs, Row, Col, Statistic, DatePicker, Button, Table, Radio, Space, Select, Divider, message } from 'antd';
import { BarChartOutlined, LineChartOutlined, PieChartOutlined, AreaChartOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Statistics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState('bar');
  const [dateRange, setDateRange] = useState([moment().subtract(7, 'days'), moment()]);
  const [selectedCamera, setSelectedCamera] = useState('all');

  // 模拟摄像头数据
  const cameraOptions = [
    { label: '所有摄像头', value: 'all' },
    { label: '前门摄像头', value: 'camera1' },
    { label: '后门摄像头', value: 'camera2' },
    { label: '大厅摄像头', value: 'camera3' },
    { label: '停车场摄像头', value: 'camera4' },
  ];

  // 模拟统计数据
  const statisticsData = {
    totalPeople: 12589,
    todayPeople: 1245,
    averagePeople: 1350,
    maxPeople: 1890,
    totalWarnings: 156,
    todayWarnings: 12,
    processedWarnings: 142,
    pendingWarnings: 14,
    totalCameras: 24,
    onlineCameras: 22,
    offlineCameras: 2,
    totalPush: 356,
    smsPush: 125,
    officePush: 156,
    apiPush: 45,
    voicePush: 30,
  };

  // 模拟人流量数据
  const generatePeopleFlowData = () => {
    const days = 7;
    const data = [];
    const now = moment();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = moment(now).subtract(i, 'days').format('MM-DD');
      data.push({
        date,
        count: Math.floor(Math.random() * 500) + 1000,
      });
    }
    
    return data;
  };

  // 模拟警告数据
  const generateWarningData = () => {
    const days = 7;
    const data = [];
    const now = moment();
    const types = ['人员检测', '火灾检测', '异常行为', '区域入侵'];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = moment(now).subtract(i, 'days').format('MM-DD');
      const dayData = {
        date,
        total: 0,
      };
      
      types.forEach(type => {
        const count = Math.floor(Math.random() * 10);
        dayData[type] = count;
        dayData.total += count;
      });
      
      data.push(dayData);
    }
    
    return data;
  };

  // 模拟推送数据
  const generatePushData = () => {
    const days = 7;
    const data = [];
    const now = moment();
    const types = ['短信推送', '办公推送', '接口推送', '语音推送'];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = moment(now).subtract(i, 'days').format('MM-DD');
      const dayData = {
        date,
        total: 0,
      };
      
      types.forEach(type => {
        const count = Math.floor(Math.random() * 15);
        dayData[type] = count;
        dayData.total += count;
      });
      
      data.push(dayData);
    }
    
    return data;
  };

  // 模拟摄像头状态数据
  const generateCameraStatusData = () => {
    return [
      { value: statisticsData.onlineCameras, name: '在线' },
      { value: statisticsData.offlineCameras, name: '离线' },
    ];
  };

  // 模拟警告类型分布数据
  const generateWarningTypeData = () => {
    return [
      { value: 65, name: '人员检测' },
      { value: 25, name: '火灾检测' },
      { value: 46, name: '异常行为' },
      { value: 20, name: '区域入侵' },
    ];
  };

  // 模拟推送类型分布数据
  const generatePushTypeData = () => {
    return [
      { value: statisticsData.smsPush, name: '短信推送' },
      { value: statisticsData.officePush, name: '办公推送' },
      { value: statisticsData.apiPush, name: '接口推送' },
      { value: statisticsData.voicePush, name: '语音推送' },
    ];
  };

  // 人流量数据
  const peopleFlowData = generatePeopleFlowData();
  
  // 警告数据
  const warningData = generateWarningData();
  
  // 推送数据
  const pushData = generatePushData();
  
  // 摄像头状态数据
  const cameraStatusData = generateCameraStatusData();
  
  // 警告类型分布数据
  const warningTypeData = generateWarningTypeData();
  
  // 推送类型分布数据
  const pushTypeData = generatePushTypeData();

  // 人流量图表选项
  const getPeopleFlowChartOption = () => {
    const xAxisData = peopleFlowData.map(item => item.date);
    const seriesData = peopleFlowData.map(item => item.count);
    
    let option = {
      title: {
        text: '人流量统计',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
      },
      yAxis: {
        type: 'value',
        name: '人数',
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {},
        },
      },
    };
    
    if (chartType === 'bar') {
      option.series = [{
        data: seriesData,
        type: 'bar',
        name: '人流量',
        itemStyle: {
          color: '#1890ff',
        },
      }];
    } else if (chartType === 'line') {
      option.series = [{
        data: seriesData,
        type: 'line',
        name: '人流量',
        smooth: true,
        itemStyle: {
          color: '#1890ff',
        },
        areaStyle: {},
      }];
    }
    
    return option;
  };

  // 警告统计图表选项
  const getWarningChartOption = () => {
    const xAxisData = warningData.map(item => item.date);
    const types = ['人员检测', '火灾检测', '异常行为', '区域入侵'];
    const series = types.map(type => ({
      name: type,
      type: chartType === 'bar' ? 'bar' : 'line',
      stack: chartType === 'bar' ? '总量' : undefined,
      data: warningData.map(item => item[type]),
      smooth: chartType === 'line',
    }));
    
    return {
      title: {
        text: '警告统计',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: types,
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {},
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
      },
      yAxis: {
        type: 'value',
        name: '数量',
      },
      series: series,
    };
  };

  // 推送统计图表选项
  const getPushChartOption = () => {
    const xAxisData = pushData.map(item => item.date);
    const types = ['短信推送', '办公推送', '接口推送', '语音推送'];
    const series = types.map(type => ({
      name: type,
      type: chartType === 'bar' ? 'bar' : 'line',
      stack: chartType === 'bar' ? '总量' : undefined,
      data: pushData.map(item => item[type]),
      smooth: chartType === 'line',
    }));
    
    return {
      title: {
        text: '推送统计',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: types,
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {},
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
      },
      yAxis: {
        type: 'value',
        name: '数量',
      },
      series: series,
    };
  };

  // 摄像头状态图表选项
  const getCameraStatusChartOption = () => {
    return {
      title: {
        text: '摄像头状态',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 10,
        data: ['在线', '离线'],
      },
      series: [
        {
          name: '摄像头状态',
          type: 'pie',
          radius: ['50%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '18',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: cameraStatusData,
          itemStyle: {
            color: function(params) {
              const colorList = ['#52c41a', '#ff4d4f'];
              return colorList[params.dataIndex];
            },
          },
        },
      ],
    };
  };

  // 警告类型分布图表选项
  const getWarningTypeChartOption = () => {
    return {
      title: {
        text: '警告类型分布',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 10,
        data: warningTypeData.map(item => item.name),
      },
      series: [
        {
          name: '警告类型',
          type: 'pie',
          radius: ['50%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '18',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: warningTypeData,
        },
      ],
    };
  };

  // 推送类型分布图表选项
  const getPushTypeChartOption = () => {
    return {
      title: {
        text: '推送类型分布',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 10,
        data: pushTypeData.map(item => item.name),
      },
      series: [
        {
          name: '推送类型',
          type: 'pie',
          radius: ['50%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '18',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: pushTypeData,
        },
      ],
    };
  };

  // 处理日期范围变化
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // 实际项目中这里应该重新获取数据
  };

  // 处理刷新数据
  const handleRefresh = () => {
    setLoading(true);
    // 模拟数据加载
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  // 处理导出数据
  const handleExport = () => {
    message.success('数据导出成功');
  };

  // 处理摄像头选择变化
  const handleCameraChange = (value) => {
    setSelectedCamera(value);
    // 实际项目中这里应该重新获取数据
  };

  return (
    <div>
      <Card title={
        <span>
          <BarChartOutlined style={{ marginRight: 8 }} />
          统计分析
        </span>
      }>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={
              <span>
                <AreaChartOutlined />
                总览
              </span>
            } 
            key="overview"
          >
            <div className="statistics-header" style={{ marginBottom: 20 }}>
              <Space>
                <RangePicker 
                  value={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleRefresh}
                  loading={loading}
                >
                  刷新
                </Button>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                >
                  导出数据
                </Button>
              </Space>
            </div>

            <Row gutter={16}>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="总人流量" 
                    value={statisticsData.totalPeople} 
                    suffix="人" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="今日人流量" 
                    value={statisticsData.todayPeople} 
                    suffix="人" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="平均人流量" 
                    value={statisticsData.averagePeople} 
                    suffix="人/天" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="最高人流量" 
                    value={statisticsData.maxPeople} 
                    suffix="人/天" 
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="总警告数" 
                    value={statisticsData.totalWarnings} 
                    suffix="条" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="今日警告" 
                    value={statisticsData.todayWarnings} 
                    suffix="条" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="已处理警告" 
                    value={statisticsData.processedWarnings} 
                    suffix="条" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="待处理警告" 
                    value={statisticsData.pendingWarnings} 
                    suffix="条" 
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="摄像头总数" 
                    value={statisticsData.totalCameras} 
                    suffix="台" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="在线摄像头" 
                    value={statisticsData.onlineCameras} 
                    suffix="台" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="离线摄像头" 
                    value={statisticsData.offlineCameras} 
                    suffix="台" 
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="总推送数" 
                    value={statisticsData.totalPush} 
                    suffix="条" 
                  />
                </Card>
              </Col>
            </Row>

            <Divider>分布统计</Divider>

            <Row gutter={16}>
              <Col span={8}>
                <ReactECharts option={getCameraStatusChartOption()} style={{ height: 300 }} />
              </Col>
              <Col span={8}>
                <ReactECharts option={getWarningTypeChartOption()} style={{ height: 300 }} />
              </Col>
              <Col span={8}>
                <ReactECharts option={getPushTypeChartOption()} style={{ height: 300 }} />
              </Col>
            </Row>
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <LineChartOutlined />
                人流量统计
              </span>
            } 
            key="peopleFlow"
          >
            <div className="statistics-header" style={{ marginBottom: 20 }}>
              <Space>
                <RangePicker 
                  value={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <Select 
                  value={selectedCamera} 
                  onChange={handleCameraChange}
                  style={{ width: 150 }}
                >
                  {cameraOptions.map(option => (
                    <Option key={option.value} value={option.value}>{option.label}</Option>
                  ))}
                </Select>
                <Radio.Group value={chartType} onChange={e => setChartType(e.target.value)}>
                  <Radio.Button value="bar"><BarChartOutlined /> 柱状图</Radio.Button>
                  <Radio.Button value="line"><LineChartOutlined /> 折线图</Radio.Button>
                </Radio.Group>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleRefresh}
                  loading={loading}
                >
                  刷新
                </Button>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                >
                  导出数据
                </Button>
              </Space>
            </div>

            <ReactECharts option={getPeopleFlowChartOption()} style={{ height: 400 }} />

            <Divider>详细数据</Divider>

            <Table 
              columns={[
                { title: '日期', dataIndex: 'date', key: 'date' },
                { title: '人流量', dataIndex: 'count', key: 'count' },
              ]}
              dataSource={peopleFlowData.map((item, index) => ({ ...item, key: index }))}
              pagination={false}
              loading={loading}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <LineChartOutlined />
                警告统计
              </span>
            } 
            key="warning"
          >
            <div className="statistics-header" style={{ marginBottom: 20 }}>
              <Space>
                <RangePicker 
                  value={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <Radio.Group value={chartType} onChange={e => setChartType(e.target.value)}>
                  <Radio.Button value="bar"><BarChartOutlined /> 柱状图</Radio.Button>
                  <Radio.Button value="line"><LineChartOutlined /> 折线图</Radio.Button>
                </Radio.Group>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleRefresh}
                  loading={loading}
                >
                  刷新
                </Button>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                >
                  导出数据
                </Button>
              </Space>
            </div>

            <ReactECharts option={getWarningChartOption()} style={{ height: 400 }} />

            <Divider>详细数据</Divider>

            <Table 
              columns={[
                { title: '日期', dataIndex: 'date', key: 'date' },
                { title: '人员检测', dataIndex: '人员检测', key: '人员检测' },
                { title: '火灾检测', dataIndex: '火灾检测', key: '火灾检测' },
                { title: '异常行为', dataIndex: '异常行为', key: '异常行为' },
                { title: '区域入侵', dataIndex: '区域入侵', key: '区域入侵' },
                { title: '总计', dataIndex: 'total', key: 'total' },
              ]}
              dataSource={warningData.map((item, index) => ({ ...item, key: index }))}
              pagination={false}
              loading={loading}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <LineChartOutlined />
                推送统计
              </span>
            } 
            key="push"
          >
            <div className="statistics-header" style={{ marginBottom: 20 }}>
              <Space>
                <RangePicker 
                  value={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <Radio.Group value={chartType} onChange={e => setChartType(e.target.value)}>
                  <Radio.Button value="bar"><BarChartOutlined /> 柱状图</Radio.Button>
                  <Radio.Button value="line"><LineChartOutlined /> 折线图</Radio.Button>
                </Radio.Group>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleRefresh}
                  loading={loading}
                >
                  刷新
                </Button>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                >
                  导出数据
                </Button>
              </Space>
            </div>

            <ReactECharts option={getPushChartOption()} style={{ height: 400 }} />

            <Divider>详细数据</Divider>

            <Table 
              columns={[
                { title: '日期', dataIndex: 'date', key: 'date' },
                { title: '短信推送', dataIndex: '短信推送', key: '短信推送' },
                { title: '办公推送', dataIndex: '办公推送', key: '办公推送' },
                { title: '接口推送', dataIndex: '接口推送', key: '接口推送' },
                { title: '语音推送', dataIndex: '语音推送', key: '语音推送' },
                { title: '总计', dataIndex: 'total', key: 'total' },
              ]}
              dataSource={pushData.map((item, index) => ({ ...item, key: index }))}
              pagination={false}
              loading={loading}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default Statistics;
